import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, TrendingUp, Building2, Home, DollarSign, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactFormPopup from '../components/ContactFormPopup';

const AreaDetail = () => {
  const { areaId } = useParams();
  const [showPopup, setShowPopup] = React.useState(false);

  const areaDetails = {
    'dubai-south': {
      name: 'Dubai South',
      tagline: 'Future-Ready Smart City Hub',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/dubai_south_wmclrt.png',
      description: 'Dubai South represents the future of urban living in Dubai. Located near the Expo 2020 site and Al Maktoum International Airport, this ambitious development is set to become one of the world\'s most advanced smart cities. With state-of-the-art infrastructure, sustainable design, and unmatched connectivity, Dubai South offers investors a unique opportunity to be part of Dubai\'s next growth chapter.',
      keyFeatures: [
        { icon: TrendingUp, title: 'High Growth Potential', desc: 'Strategic location near Expo 2020 legacy projects' },
        { icon: Building2, title: 'Smart Infrastructure', desc: 'IoT-enabled buildings and sustainable design' },
        { icon: Home, title: 'Mixed-Use Development', desc: 'Residential, commercial, and leisure spaces' },
        { icon: DollarSign, title: 'Competitive Pricing', desc: 'Properties starting from AED 600K' },
      ],
      stats: [
        { value: '145 km²', label: 'Total Area' },
        { value: '1M+', label: 'Projected Population' },
        { value: '500K+', label: 'Future Jobs' },
        { value: '2025+', label: 'Full Development' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805799/dubai_south_area_1_n8gpd0.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805800/dubai_south_area_2_vpjq3a.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805800/duabi_south_area_3_eexkr8.png',
      ]
    },
    'furjan': {
      name: 'Furjan',
      tagline: 'Family-Oriented Premium Community',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/Furjan_cn9ehj.png',
      description: 'Furjan is a master-planned residential community that perfectly balances modern living with family values. Developed by Nakheel, this vibrant neighborhood offers spacious villas and townhouses with contemporary designs, surrounded by lush green spaces, parks, and world-class amenities. Ideal for families seeking a peaceful yet connected lifestyle.',
      keyFeatures: [
        { icon: Home, title: 'Premium Villas', desc: '3-5 bedroom villas with modern architecture' },
        { icon: TrendingUp, title: 'Community Living', desc: 'Parks, schools, and retail within community' },
        { icon: Building2, title: 'Quality Construction', desc: 'Built by Nakheel with premium finishes' },
        { icon: DollarSign, title: 'Value Investment', desc: 'Starting from AED 1.2M' },
      ],
      stats: [
        { value: '3,000+', label: 'Residential Units' },
        { value: '12', label: 'Parks & Gardens' },
        { value: '5 min', label: 'To Metro Station' },
        { value: '20 min', label: 'To Downtown' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806134/furjan_area_3_wzu9dm.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806134/furjan_area_1_mzcb63.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806133/furjan_area_2_gdkqfu.png',
      ]
    },
    'dubai-land': {
      name: 'Dubai Land',
      tagline: 'Massive Mixed-Use Entertainment Hub',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/DUBAI_LAND_y3vvy6.png',
      description: 'Dubai Land is one of the world\'s largest integrated lifestyle and entertainment destinations. This massive development encompasses residential communities, theme parks, retail centers, and sports facilities. With its strategic location and diverse offerings, Dubai Land presents investors with multiple opportunities across different property types and price points.',
      keyFeatures: [
        { icon: Building2, title: 'Mixed-Use', desc: 'Residential, retail, entertainment all integrated' },
        { icon: TrendingUp, title: 'High Demand', desc: 'Strong rental yields and capital appreciation' },
        { icon: Home, title: 'Variety', desc: 'Apartments, villas, townhouses available' },
        { icon: DollarSign, title: 'Affordable', desc: 'Entry points from AED 450K' },
      ],
      stats: [
        { value: '278 km²', label: 'Total Area' },
        { value: '45+', label: 'Projects' },
        { value: '3M+', label: 'Visitors Annually' },
        { value: '2024+', label: 'Full Completion' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806913/Dubai_land_area_2_tti3p8.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806914/Dubai_Land_area_1_ijhrlc.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806913/Dubai_land_area_3_eixgfa.png',
      ]
    },
    'jbr': {
      name: 'Jumeirah Beach Residence (JBR)',
      tagline: 'Iconic Beachfront Living',
      hero: 'https://images.unsplash.com/photo-1650376126635-1304b38cb933',
      description: 'JBR is Dubai\'s premier beachfront community, offering a unique blend of luxury living, dining, and entertainment. With pristine beaches, waterfront promenades, and stunning marina views, JBR provides an unmatched coastal lifestyle. This highly sought-after location guarantees strong rental demand and excellent investment returns.',
      keyFeatures: [
        { icon: Home, title: 'Beach Living', desc: 'Direct beach access and waterfront views' },
        { icon: TrendingUp, title: 'High Demand', desc: 'Year-round tourist and resident interest' },
        { icon: Building2, title: 'Luxury Towers', desc: '40+ residential and hotel towers' },
        { icon: DollarSign, title: 'Premium Investment', desc: 'Starting from AED 1.5M' },
      ],
      stats: [
        { value: '6,917', label: 'Residential Units' },
        { value: '1.7 km', label: 'Beachfront Walk' },
        { value: '200+', label: 'Retail Outlets' },
        { value: '15 min', label: 'To Palm Jumeirah' },
      ],
      images: [
        'https://images.unsplash.com/photo-1650376126635-1304b38cb933',
        'https://images.unsplash.com/photo-1603520781806-dbedf904bf32',
        'https://images.unsplash.com/photo-1604667758760-ffb2931593a0',
      ]
    },
    'wasl-gate': {
      name: 'Wasl Gate',
      tagline: 'Ultra-Modern Central Living',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/wasl_gate_vbri7v.png',
      description: 'Wasl Gate represents contemporary urban living at its finest. Located in the heart of Dubai with easy access to major business districts and leisure destinations, this development features sleek architectural design and premium finishes. Perfect for young professionals and investors seeking a central, well-connected location.',
      keyFeatures: [
        { icon: MapPin, title: 'Central Location', desc: 'Minutes from Downtown and Business Bay' },
        { icon: Building2, title: 'Modern Design', desc: 'Contemporary architecture with luxury finishes' },
        { icon: TrendingUp, title: 'High ROI', desc: 'Strong rental yields in prime location' },
        { icon: DollarSign, title: 'Investment Value', desc: 'From AED 980K' },
      ],
      stats: [
        { value: '5 min', label: 'To Downtown' },
        { value: '10 min', label: 'To Business Bay' },
        { value: '15 min', label: 'To Dubai Mall' },
        { value: '2025', label: 'Completion Year' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806375/wasl_gate_area_3_xizgak.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806375/wasl_gate_area_1_qhkhzt.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776806376/wasl_gate_are_2_ll57qs.png',
      ]
    },
  };

  const area = areaDetails[areaId] || areaDetails['dubai-south'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={area.hero} alt={area.name} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/70 to-white" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-600 hover:text-yellow-700 font-semibold mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-4">{area.name}</h1>
            <p className="text-2xl text-yellow-600 font-semibold mb-8">{area.tagline}</p>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl">{area.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {area.stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-yellow-50 to-white rounded-2xl border border-yellow-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl font-bold text-yellow-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {area.keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="flex gap-6 p-8 bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Gallery</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {area.images.map((image, index) => (
              <motion.div
                key={index}
                className="relative h-80 rounded-2xl overflow-hidden shadow-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={image} alt={`${area.name} ${index + 1}`} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Ready to Invest in {area.name}?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Schedule a free consultation with our experts to explore investment opportunities in this prime location.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <motion.button
              onClick={() => setShowPopup(true)}
              className="px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Inventory
            </motion.button>
            <motion.button
              onClick={() => setShowPopup(true)}
              className="px-10 py-5 border-2 border-yellow-500 text-yellow-700 font-bold text-lg rounded-full hover:bg-yellow-50 transition-all duration-300"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
            >
              Show Available Units
            </motion.button>
          </div>
        </div>
      </section>

      <ContactFormPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        formType="inventory"
      />
      <Footer />
    </div>
  );
};

export default AreaDetail;
